import { apiConnection } from "@/services/api-connection";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";
import { i18n } from "@/i18n";
import { notificationManager } from "@/services/utilities/notification-manager";
import { accessManager } from "@/services/security/access-manager";
import { studeaManagerManager } from "@/services/studea-manager/studea-manager-manager";

const URL_LIST = "/app/triad/list/";
const URL_ONE = "/app/triad/";
const URL_PDF = "/app/triad/pdf/";
const URL_CONTRACT_RUPTURE_FILE = "/app/contract/rupture/file/download/";
const URL_STUDEA_MANAGER_CONTRACT_RUPTURE = "/app/studea-manager/contract/rupture/";

class TriadManager {
  APPRENTICE_PROFILE = 1;
  TUTOR_PROFILE = 2;
  APPRENTICE_MASTER_PROFILE = 3;
  STUDEA_MANAGER_PROFILE = 4;
  STUDEA_ADMIN_PROFILE = 5;
  KEEP_DUPLICATE_USER = 1;
  DELETE_DUPLICATE_USER = 0;

  loadTriadList = (id, archive, search = "") => {
    return apiConnection
      .get(URL_LIST + id, { archive: archive, search: search }, true)
      .then((response) => response?.crews);
  };
  loadOne = (id) => {
    return apiConnection.get(URL_ONE + id, {}, true).then((response) => response);
  };

  pdf(id, keyDates = false, zip = false) {
    return apiConnection.get(URL_PDF + id, { keyDates: keyDates ? 1 : 0, zip: zip ? 1 : 0 }).then((res) => {
      messengerMessageManager.processMessage(res);
    });
  }

  triggerFileDownload(downloadUrl, notificationManagerService = notificationManager) {
    const link = document.createElement("a");
    link.setAttribute("href", downloadUrl);
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notificationManagerService.showNotification("success", i18n.global.t("download_success"));
  }

  downlaodContractRuptureFile(id, data, notificationManagerService = notificationManager) {
    return apiConnection.post(URL_CONTRACT_RUPTURE_FILE + id, data).then((res) => {
      if (res.downloadUrl) {
        this.triggerFileDownload(res.downloadUrl, notificationManagerService);
      }
    });
  }

  async downloadStudeaManagerContractRupture(contractId, data, notificationManagerService = notificationManager) {
    const res = await apiConnection.post(URL_STUDEA_MANAGER_CONTRACT_RUPTURE + contractId + "/download", data);
    if (res?.downloadUrl) {
      this.triggerFileDownload(res.downloadUrl, notificationManagerService);
    }
  }

  // ST154-TA-4900 : le filtre "Archivées" des activités n'est pas proposé au tuteur (2) ni au
  // maître d'apprentissage (3). Formulé en exclusion et non en liste blanche : un utilisateur sans
  // profil de triade résolu (studea manager, admin, prévisualisation côté éditeur) doit continuer
  // à voir le filtre.
  userCanFilterArchivedContractActivities(triad) {
    const roles = triad?.userRoles || [];

    return !roles.includes(this.TUTOR_PROFILE) && !roles.includes(this.APPRENTICE_MASTER_PROFILE);
  }

  userCanAddTriadDocument(triad) {
    const roles = triad?.userRoles || [];

    return (
      !!triad?.active &&
      [this.APPRENTICE_PROFILE, this.TUTOR_PROFILE, this.APPRENTICE_MASTER_PROFILE].some((profile) =>
        roles.includes(profile),
      )
    );
  }

  userCanSeeQuestionnaireMark(profile, questionnaire) {
    return (
      profile === this.STUDEA_MANAGER_PROFILE ||
      profile === this.STUDEA_ADMIN_PROFILE ||
      !!(
        questionnaire &&
        questionnaire.marksVisibleBy &&
        Array.isArray(questionnaire.marksVisibleBy) &&
        questionnaire.marksVisibleBy.includes(profile)
      )
    );
  }

  setProfileLabel(
    key,
    apprenticeProfileLabel = {},
    tutorProfileLabel = {},
    apprenticeMasterProfileLabel = {},
    studeaManagerProfileLabel = {},
  ) {
    switch (key) {
      case this.APPRENTICE_PROFILE:
        return apprenticeProfileLabel?.title ? apprenticeProfileLabel.title : i18n.global.t("apprentice");
      case this.TUTOR_PROFILE:
        return tutorProfileLabel?.title ? tutorProfileLabel.title : i18n.global.t("pedagogic_tutor");
      case this.APPRENTICE_MASTER_PROFILE:
        return apprenticeMasterProfileLabel?.title
          ? apprenticeMasterProfileLabel.title
          : i18n.global.t("apprenticeMaster");
      case this.STUDEA_MANAGER_PROFILE:
        return studeaManagerProfileLabel?.title ? studeaManagerProfileLabel.title : i18n.global.t("studeaManager");
    }
  }

  // Contrairement à setProfileLabel, ne retourne jamais de repli i18n : certains affichages
  // (ex. QuestionnaireHeadingComponent) doivent rester silencieux tant que la plateforme n'a
  // pas configuré de libellé personnalisé pour ce profil.
  getProfileLabelStrict(
    profile,
    apprenticeProfileLabel = {},
    tutorProfileLabel = {},
    apprenticeMasterProfileLabel = {},
    studeaManagerProfileLabel = {},
  ) {
    switch (profile) {
      case this.APPRENTICE_PROFILE:
        return apprenticeProfileLabel?.title;
      case this.TUTOR_PROFILE:
        return tutorProfileLabel?.title;
      case this.APPRENTICE_MASTER_PROFILE:
        return apprenticeMasterProfileLabel?.title;
      case this.STUDEA_MANAGER_PROFILE:
        return studeaManagerProfileLabel?.title;
    }
  }

  getProfileAbbreviation(
    profile,
    apprenticeProfileLabel = {},
    tutorProfileLabel = {},
    apprenticeMasterProfileLabel = {},
    studeaManagerProfileLabel = {},
    fallback = "",
  ) {
    let label;
    switch (profile) {
      case this.APPRENTICE_PROFILE:
        label = apprenticeProfileLabel;
        break;
      case this.TUTOR_PROFILE:
        label = tutorProfileLabel;
        break;
      case this.APPRENTICE_MASTER_PROFILE:
        label = apprenticeMasterProfileLabel;
        break;
      case this.STUDEA_MANAGER_PROFILE:
        label = studeaManagerProfileLabel;
        break;
    }
    return label?.titleAbbreviation ? label.titleAbbreviation.toUpperCase() : fallback;
  }

  getProfileModifierClass(profile) {
    switch (profile) {
      case this.APPRENTICE_PROFILE:
        return "-apprentice";
      case this.TUTOR_PROFILE:
        return "-tutor";
      case this.APPRENTICE_MASTER_PROFILE:
        return "-apprenticeMaster";
      case this.STUDEA_MANAGER_PROFILE:
        return "-studeaManager";
      default:
        return "";
    }
  }

  getContractRuptureDateForm(contractId) {
    return apiConnection.get("/app/contract/rupture/form/" + contractId).then((res) => res);
  }

  postContractRuptureDateForm(contractId, data) {
    return apiConnection.post("/app/contract/rupture/post/" + contractId, data);
  }

  getStudeaManagerContractRuptureForm(contractId) {
    return apiConnection.get(URL_STUDEA_MANAGER_CONTRACT_RUPTURE + contractId + "/form");
  }

  postStudeaManagerContractRupture(contractId, data) {
    return apiConnection.post(URL_STUDEA_MANAGER_CONTRACT_RUPTURE + contractId + "/post", data);
  }

  validationContractRuptureJustification(contractId, invalid = false) {
    return apiConnection.get("/app/contract/rupture/" + (invalid ? "in" : "") + "validate-justification/" + contractId);
  }

  getTriadInfoTitle(item) {
    switch (item.type) {
      case "presentation":
        return item.label ? item.label : i18n.global.t("dashboard.presentation");
      case "contact":
        return item.label ? item.label : i18n.global.t("dashboard.contact");
      case "partner":
        return item.label ? item.label : i18n.global.t("dashboard.partner");
      case "communication":
        return item.label ? item.label : i18n.global.t("dashboard.communication");
    }
  }

  getTriadInfoIcon(type) {
    switch (type) {
      case "presentation":
        return "presentation-screen";
      case "contact":
        return "id-card-clip";
      case "partner":
        return "handshake";
      case "communication":
        return "megaphone";
    }
  }

  async cancelContractRupture(contractId) {
    return await apiConnection.put("/app/contract/rupture/put/reinit/" + contractId);
  }

  displayRuptureButton(contract, trainingCourse, triad, user) {
    const access = accessManager.ALL;
    const code = studeaManagerManager.STUDEA_MANAGER_TRIAD_CONTRACT_RUPTURE_VALIDATOR;

    if (user?.isAdmin || accessManager.isAvailableForUser(null, code, access)) {
      return (
        contract.activeContract &&
        !!contract.ruptureDate?.length &&
        !contract.ruptureJustificationValidated &&
        trainingCourse?.useTriadDeclareContractRupture
      );
    }

    const roles = triad?.userRoles || [];
    return (
      contract.activeContract &&
      (roles.includes(triadManager.APPRENTICE_PROFILE) || roles.includes(triadManager.APPRENTICE_MASTER_PROFILE)) &&
      !contract.ruptureJustificationValidated &&
      trainingCourse?.useTriadDeclareContractRupture
    );
  }
}

export const triadManager = new TriadManager();
